/**
 * Панель действий карточки видео-референса (спека §12): статус (в работе — спиннер, ошибка — «Повторить»),
 * «Переразобрать» пресетом с подтверждением (ручные правки теряются), «Описать сцены ≈ $» (ПЛАТНО, ключ —
 * `useDescribe`) и «Создать проект» (неактивна, пока `projectProblems` не пуст; ПЛАТНО — в окне).
 */
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Btn, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { Segmented } from "@/components/trends/ui";
import { CreateProjectDialog } from "@/components/videoref/CreateProjectDialog";
import { errorText } from "@/lib/project";
import { inProgress, presetOf, projectProblems, STATUS_LABEL, type Preset, type VideoRefDetail } from "@/lib/videoRef";
import { refErrorText, useDescribe, useReanalyze } from "@/lib/videoRefApi";

const PRESET_LABEL: Record<Preset, string> = { more: "больше сцен", normal: "обычно", fewer: "меньше сцен" };

/** Статус, «Переразобрать» (с подтверждением), «Описать сцены ≈ $» и «Создать проект». */
export function RefActions({ detail, beforePaid }: { detail: VideoRefDetail; beforePaid: () => Promise<void> }) {
  const info = detail.ref;
  const reanalyze = useReanalyze(info.id);
  const describe = useDescribe(info.id);
  const [confirm, setConfirm] = useState<Preset | null>(null);
  const [creating, setCreating] = useState(false);
  const [prepError, setPrepError] = useState("");

  const live = inProgress(info.status);
  const ready = info.status === "ready";
  const problems = projectProblems(detail.scenes, detail.limits);
  const current = presetOf(info.split?.threshold, detail.limits.presets) ?? "none";

  const runDescribe = async () => {
    if (describe.busy) return;
    setPrepError("");
    try {
      await beforePaid();
    } catch (e) {
      setPrepError(`комментарии не сохранились: ${errorText(e)}`);
      return;
    }
    describe.run();
  };

  return (
    <Panel annot="видео-референс: действия">
      <div className="grid gap-3">
        {live ? (
          <div className="flex items-center gap-2 text-[12.5px] text-fg-muted">
            <Loader2 size={14} className="animate-spin" />
            {STATUS_LABEL[info.status]}
            {info.task?.status === "queued" ? <Hint>ждёт очереди сбора</Hint> : null}
          </div>
        ) : null}
        {info.status === "failed" ? (
          <div className="flex flex-wrap items-start gap-2">
            <ErrorLine text={info.error || info.task?.error || "ошибка"} className="min-w-0 flex-1" />
            <Btn onClick={() => reanalyze.mutate(undefined)} disabled={reanalyze.isPending}>
              Повторить
            </Btn>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Segmented<Preset | "none">
            label="Переразобрать"
            value={confirm ?? current}
            onPick={(v) => v !== "none" && setConfirm(v)}
            options={(["more", "normal", "fewer"] as const).map((p) => ({
              value: p,
              label: PRESET_LABEL[p],
              disabled: live || reanalyze.isPending,
            }))}
            annot="видео-референс: переразобрать"
          />
        </div>
        {confirm ? (
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-warn/40 bg-bg px-3 py-2 text-[12.5px]">
            <span className="text-fg">
              Переразобрать ({PRESET_LABEL[confirm]})? Ручные правки и отметки будут потеряны.
            </span>
            <Btn
              tone="danger"
              onClick={() => {
                reanalyze.mutate(confirm);
                setConfirm(null);
              }}
            >
              Переразобрать
            </Btn>
            <Btn tone="ghost" onClick={() => setConfirm(null)}>
              Отмена
            </Btn>
          </div>
        ) : null}
        <ErrorLine text={reanalyze.isError ? refErrorText(reanalyze.error) : ""} />

        <div className="flex flex-wrap items-center gap-2">
          <Btn
            onClick={() => void runDescribe()}
            disabled={!ready || detail.scenes.length === 0 || describe.busy}
            title="платно: композер опишет форму каждой сцены"
            data-annot="видео-референс: описать сцены"
          >
            {describe.busy ? <Loader2 size={13} className="animate-spin" /> : null}
            {describe.busy ? "Описываю…" : info.summary ? "Описать заново" : "Описать сцены"} ≈ $
            {detail.estimate.describe.toFixed(2)}
          </Btn>
          <Btn
            tone="primary"
            onClick={() => setCreating(true)}
            disabled={!ready || problems.length > 0}
            title="платно: композер составит сценарий по форме референса"
            data-annot="видео-референс: создать проект"
          >
            Создать проект
          </Btn>
        </div>
        {info.describe_stale ? <Hint>описание сделано до правок — повторить</Hint> : null}
        {ready && problems.length > 0 ? <Hint>Создать проект: {problems.join("; ")}</Hint> : null}
        <ErrorLine text={prepError || (describe.failure ? refErrorText(describe.failure) : "")} />
      </div>

      <CreateProjectDialog
        open={creating}
        onClose={() => setCreating(false)}
        refId={info.id}
        scenes={detail.scenes}
        limits={detail.limits}
        estimate={detail.estimate.scenario}
        beforeSend={beforePaid}
      />
    </Panel>
  );
}
